import { useEffect, useMemo, useState } from "react";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ExpensePanel } from "./ExpensePanel";
import { BcaHistory } from "./BcaHistory";

const n = (v) => parseFloat(v || 0) || 0;

export default function FinanceExpense() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [accounts, setAccounts] = useState([]);
  const [fundRows, setFundRows] = useState([]);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const loadBalances = () => {
    api.get("/accounts")
      .then(({ data }) => {
        const nextAccounts = Array.isArray(data) ? data : [];
        setAccounts(nextAccounts);
        setFundRows(nextAccounts.map((x) => ({
          account_id: x.account_id,
          account_name: x.account_name,
          account_type: x.account_type,
          physical_balance: x.physical_balance ?? x.current_balance ?? "0",
          modal_available: x.modal_available ?? "0",
          profit_available: x.profit_available ?? "0",
          unclassified_available: x.unclassified_available ?? "0",
        })));
      })
      .catch(() => {});
  };

  const refreshAll = () => {
    loadBalances();
    setHistoryRefresh((x) => x + 1);
  };

  useEffect(() => { if (isOwner) loadBalances(); }, [isOwner]);

  const banks = useMemo(() => {
    const map = new Map(fundRows.map((r) => [r.account_id, r]));
    return accounts
      .filter((a) => a.account_type === "BANK" && String(a.is_active) === "true")
      .map((a) => ({ ...a, ...(map.get(a.account_id) || {}) }));
  }, [accounts, fundRows]);

  const totals = banks.reduce((acc, b) => ({
    physical: acc.physical + n(b.physical_balance ?? b.current_balance),
    modal: acc.modal + n(b.modal_available),
    profit: acc.profit + n(b.profit_available),
    uncl: acc.uncl + n(b.unclassified_available),
  }), { physical: 0, modal: 0, profit: 0, uncl: 0 });

  if (user && !isOwner) {
    return (<div className="app-shell"><AppTopBar active="finance-expense" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  return (
    <div className="app-shell">
      <AppTopBar active="finance-expense" />
      <main className="content">
        <div style={{ width: "100%", maxWidth: 1052, margin: "0 auto" }}>
          <div style={{ marginBottom: 18 }}>
            <h1 style={{ margin: "0 0 6px", fontSize: 26 }}>BCA OUT / Biaya</h1>
            <p className="hint" style={{ margin: 0 }}>Transaksi harian pengeluaran dari akun BANK untuk biaya operasional.</p>
          </div>

          <section className="panel" style={{ marginBottom: 16, width: "100%", boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10 }}>
              <div>
                <h2 className="panel-title" style={{ marginBottom: 4 }}>Saldo BCA Tersedia</h2>
                <div className="hint">Cek posisi dana sebelum melakukan transaksi keluar.</div>
              </div>
              <span className="hint">{banks.length} account BANK aktif</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 10, marginBottom: banks.length > 1 ? 14 : 0 }}>
              <div className="dash-fund-item"><span>SALDO FISIK</span><b>{formatIDR(totals.physical)}</b></div>
              <div className="dash-fund-item modal"><span>MODAL TERSEDIA</span><b>{formatIDR(totals.modal)}</b></div>
              <div className="dash-fund-item profit"><span>PROFIT TERSEDIA</span><b>{formatIDR(totals.profit)}</b></div>
              <div className="dash-fund-item uncl"><span>UNCLASSIFIED</span><b>{formatIDR(totals.uncl)}</b></div>
            </div>

            {banks.length > 1 && <div className="table-scroll"><table className="data-table">
              <thead><tr><th>BANK</th><th>SALDO FISIK</th><th>MODAL</th><th>PROFIT</th><th>UNCLASSIFIED</th></tr></thead>
              <tbody>{banks.map((b) => <tr key={b.account_id}><td><b>{b.account_name}</b></td><td className="mono">{formatIDR(b.physical_balance ?? b.current_balance)}</td><td className="mono">{formatIDR(b.modal_available)}</td><td className="mono">{formatIDR(b.profit_available)}</td><td className="mono">{formatIDR(b.unclassified_available)}</td></tr>)}</tbody>
            </table></div>}
          </section>

          <ExpensePanel accounts={accounts} fundRows={fundRows} onDone={refreshAll} />
          <BcaHistory accounts={accounts} refreshKey={historyRefresh} />
        </div>
      </main>
    </div>
  );
}
